import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, map, of, switchMap } from 'rxjs';
import { formatUserName } from 'server/shared/helpers/user/formatUserName';
import { CustomerTypeEnum } from 'server/shared/models/customer';
import { CustomerUserStatusEnum } from 'server/shared/models/customerUserStatusEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import { CustomerService } from 'src/app/services/customer.service';
import { UserService } from 'src/app/services/user.service';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { queryParam$ } from 'src/helpers/queryParam$';
import { ImpersonateService } from '../../../services/impersonate.service';
import { PermissionsService } from '../../../services/permissions.service';
import { ToastService } from '../../../services/toast/toast.service';
import { TranslationService } from '../../../services/translation.service';
import { ConfirmationDialog } from '../../../shared/confirmation-dialog/confirmation.component';

@Component({
  selector: 'app-customer-users-list-item',
  templateUrl: './customer-users-list-item.component.html',
  styleUrls: ['./customer-users-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerUsersListItemComponent {
  @Input() randomId?: string;
  @Input() userCount?: any;

  showDetails$ = new BehaviorSubject<boolean>(false);

  roleEnum = RoleEnum;
  i18n = this.translationService.getTranslations('user-list-item');

  // Convert static properties to observables
  loggedInUser$ = this.userService.readyDispo$;
  customer$ = this.customerService.readyDispoCustomer$;

  inputUser$ = new BehaviorSubject<User | undefined>(undefined);
  user$ = this.inputUser$.pipe(rxFilterIsNotEmpty());

  @Input() set user(user: User | undefined) {
    this.inputUser$.next(user);
  }

  formattedUserName$ = combineLatest([this.user$, this.customer$]).pipe(
    map(([user, customer]) => formatUserName(user, customer)),
  );

  // User-based permission observables
  canDeleteUser$ = this.inputUser$.pipe(
    switchMap((user) => {
      if (user?.role === RoleEnum.POOL) {
        return this.permissionsService.canDeletePoolerUsers$;
      } else if (user?.role === RoleEnum.REQUESTER) {
        return this.permissionsService.canDeleteRequesterUsers$;
      } else {
        return this.permissionsService.canDeleteDispoUsers$;
      }
    }),
  );

  // Admin check for impersonation
  isSuperAdmin$ = this.permissionsService.userIsSuperAdmin$;

  tabParam$ = queryParam$(this.route, 'tab').pipe(
    map((tab) => tab || 'ACTIVE'),
  );

  showProfileLink$ = combineLatest([this.inputUser$, this.tabParam$]).pipe(
    map(([user, tab]) => {
      return tab === 'ACTIVE' || tab === 'CREATED' || tab === 'INVITED';
    }),
  );

  canUpdateUser$ = this.inputUser$.pipe(
    switchMap((user) => {
      if (user?.role === RoleEnum.POOL) {
        return this.permissionsService.canUpdatePoolerUsers$;
      } else if (user?.role === RoleEnum.REQUESTER) {
        return this.permissionsService.canUpdateRequesterUsers$;
      } else {
        return this.permissionsService.canUpdateDispoUsers$;
      }
    }),
  );

  showResendEnrollment$ = combineLatest([
    this.inputUser$,
    this.tabParam$,
    this.customerService.dispoCustomer$,
  ]).pipe(
    switchMap(([user, tab, customer]) => {
      if (tab !== 'CREATED') {
        return of(false);
      }
      if (user?.role === RoleEnum.POOL) {
        return this.permissionsService.canCreatePoolerUsers$;
      } else if (user?.role === RoleEnum.REQUESTER) {
        return this.permissionsService.canCreateRequesterUsers$;
      } else {
        if (customer?.msalConfig) {
          return of(false);
        }
        return this.permissionsService.canCreateDispoUsers$;
      }
    }),
  );

  showReinviteRequest$ = combineLatest([this.inputUser$, this.tabParam$]).pipe(
    switchMap(([user, tab]) => {
      if (tab === 'FORMER') {
        if (user?.role === RoleEnum.POOL) {
          return this.permissionsService.canCreatePoolerUsers$;
        }
      }
      return of(false);
    }),
  );

  showJobAvailability$ = combineLatest([
    this.inputUser$,
    this.tabParam$,
    this.loggedInUser$,
  ]).pipe(
    map(([user, tab, loggedInUser]) => {
      if (user?.role !== RoleEnum.POOL || tab !== 'ACTIVE') {
        return false;
      }
      const customerId = loggedInUser?.customerId || '';
      const enableJobs = user.customerInformation?.[customerId]?.enableJobs;
      // Hide availability if enableJobs is explicitly set to false
      return enableJobs !== false;
    }),
  );

  showEmploymentAvailability$ = combineLatest([
    this.inputUser$,
    this.tabParam$,
    this.loggedInUser$,
    this.customer$,
  ]).pipe(
    map(([user, tab, loggedInUser, customer]) => {
      if (user?.role !== RoleEnum.POOL || tab !== 'ACTIVE') {
        return false;
      }
      if (
        !customer?.enableEmployments ||
        customer.customerType !== CustomerTypeEnum.AGENCY
      ) {
        return false;
      }
      const customerId = loggedInUser?.customerId || '';
      const enableEmployments =
        user.customerInformation?.[customerId]?.enableEmployments;
      // Hide employment availability if enableEmployments is explicitly set to false
      return enableEmployments !== false;
    }),
  );

  constructor(
    private dialog: MatDialog,
    private toastService: ToastService,
    private translationService: TranslationService,
    public permissionsService: PermissionsService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private userService: UserService,
    private impersonateService: ImpersonateService,
  ) {}

  isPooler(user?: User): user is PoolerUser {
    return user?.role === RoleEnum.POOL;
  }

  onNameClick(event: Event) {
    event.preventDefault();
    this.showDetails$.next(!this.showDetails$.value);
  }

  public onResendEnrollmentClick(userId: any) {
    Meteor.apply('resendEnrollment', [userId], {
      wait: false,
      onResultReceived: (error, result: any) => {
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        if (result && result.status == CustomerUserStatusEnum.REQUESTED) {
          this.toastService.add({
            type: 'SUCCESS',
            message: 'userInvitedAgain',
          });
        } else {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
        }
      },
    });
  }

  public onReinviteClick(): void {
    const user = this.inputUser$.getValue();
    if (!user) {
      return;
    }
    let name =
      user.firstName && user.lastName
        ? user.firstName + ' ' + user.lastName
        : user.email;

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: user._id,
        text: name + ' wirklich wieder in den Pool einladen?',
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        Meteor.apply('reinviteUser', [result], {
          wait: false,
          onResultReceived: (error, result: any) => {
            if (error) {
              this.toastService.add({ type: 'ERROR', message: 'serverError' });
              return;
            }
            if (result && result.status == CustomerUserStatusEnum.REQUESTED) {
              this.toastService.add({
                type: 'SUCCESS',
                message: 'userInvitedAgain',
              });
            }
          },
        });
      }
    });
  }

  public onRemoveClick(): void {
    const user = this.inputUser$.getValue();
    if (!user) {
      return;
    }
    let name =
      user.firstName && user.lastName
        ? user.firstName + ' ' + user.lastName
        : user.email;

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: user._id,
        text:
          this.i18n?.removeFromPool?.toString()?.replace('{{ name }}', name) ||
          name + ' wirklich aus dem Pool entfernen?',
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        Meteor.apply('removeUser', [result], {
          wait: false,
          onResultReceived: (error, result: any) => {
            if (error) {
              this.toastService.add({ type: 'ERROR', message: 'serverError' });
              return;
            }
            if (
              result &&
              result.status == CustomerUserStatusEnum.POOL_REMOVED
            ) {
              this.toastService.add({
                type: 'SUCCESS',
                message: 'userRemovedFromPool',
              });
            }
          },
        });
      }
    });
  }

  public onImpersonateClick(): void {
    const user = this.inputUser$.getValue();
    if (!user) {
      return;
    }
    let name =
      user.firstName && user.lastName
        ? user.firstName + ' ' + user.lastName
        : user.email;

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: user._id,
        text:
          this.i18n?.impersonateUser?.toString()?.replace('{{ name }}', name) ||
          `Als ${name} anmelden?`,
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.impersonateService.impersonateUser(result).then();
      }
    });
  }
}
